import { useLoad } from "@redotech/react-util/load";
import { getDeliveryEstimate } from "@redotech/redo-customer-portal-app/api";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExternalShipmentTracker,
  Trackable,
  TrackableFulfillment,
} from "@redotech/redo-model/trackable";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import {
  FulfillmentOrder,
  pickUpConfig,
} from "@redotech/redo-web/settings-elements/order/pick-up-config-util";
import { isAndarFulfillmentInStorePickup } from "@redotech/redo-web/settings-elements/order/trackable-status";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { getMostRecentUnrecoveredExceptionalStatus } from "../shipment-exceptions";
import { DeliveryEstimate } from "./delivery-estimate";
import * as fulfillmentCss from "./fulfillment.module.css";
import { StatusDisplay } from "./status-display";
import { TrackingCarrierInfo } from "./tracking-carrier-info";
import { TrackingDetails } from "./tracking-details";
export const TrackerCard = memo(function TrackerCard({
  tracker,
  merchantName,
  settings,
  fulfillment,
  fulfillmentOrders,
  trackable,
}: {
  tracker?: ExternalShipmentTracker;
  merchantName: string;
  settings: ReturnAppSettings;
  fulfillment?: TrackableFulfillment;
  fulfillmentOrders: FulfillmentOrder[];
  trackable: Trackable;
}) {
  const pickupConfig = pickUpConfig(fulfillmentOrders);

  const robustTracker: ExternalShipmentTracker | undefined = useMemo(() => {
    if (!tracker) return undefined;
    return {
      ...tracker,
      est_delivery_date: shouldHaveBeenDeliveredButWasnt(tracker)
        ? Temporal.Now.instant().toString()
        : tracker?.est_delivery_date,
    };
  }, [tracker]);

  const primaryEstimatedDelivery = useMemo(
    () =>
      robustTracker?.est_delivery_date
        ? new Date(robustTracker.est_delivery_date)
        : null,
    [robustTracker?.est_delivery_date],
  );

  const fallbackEstimatedDeliveryLoad = useLoad(async () => {
    if (
      robustTracker?.est_delivery_date ||
      !trackable.originalOrder.shopify.shippingAddress
    ) {
      return undefined;
    }
    try {
      const res = await getDeliveryEstimate({
        orderId: trackable.originalOrder.id,
        // @ts-expect-error: strict-null-checks
        trackingCode: robustTracker?.tracking_code,
      });

      return new Date(res.data);
    } catch (e) {
      console.error("Error fetching fallback estimated delivery", e);
    }
    return null;
  }, [robustTracker?.est_delivery_date]);

  const estimatedOrActualDelivery = useMemo(() => {
    const isDelivered = tracker?.status === "delivered";
    return isDelivered
      ? {
          isDelivered: true,
          date: new Date(
            // @ts-expect-error: strict-null-checks
            tracker.tracking_details?.find(
              (d) => d.status === "delivered",
            )?.datetime,
          ),
        }
      : {
          isDelivered: false,
          date: primaryEstimatedDelivery ?? fallbackEstimatedDeliveryLoad.value,
        };
  }, [tracker, primaryEstimatedDelivery, fallbackEstimatedDeliveryLoad.value]);

  const trackingEvents = tracker?.tracking_details?.length
    ? // @ts-expect-error: strict-null-checks
      [...robustTracker.tracking_details].reverse()
    : [];

  const mostRecentUnrecoveredExceptionalStatus =
    getMostRecentUnrecoveredExceptionalStatus(trackingEvents);

  const pastPreTransit =
    robustTracker?.tracking_details?.some((t) => t.status !== "pre_transit") ??
    false;

  const renderTrackingContent = () => {
    if (trackable.originalOrder.shopify.sourceName === "pos") {
      return null;
    }

    const customPickup = customAddressPickUpBlock({
      storeUrl: settings.storeUrl,
      fulfillment,
      isPickUpOrder: pickupConfig.isPickUpOrder,
    });

    if (customPickup) return customPickup;

    return (
      <>
        <TrackingDetails
          settings={settings}
          trackable={trackable}
          trackingEvents={trackingEvents}
        />
        <TrackingCarrierInfo
          fulfillment={fulfillment}
          // @ts-expect-error: strict-null-checks
          robustTracker={robustTracker}
          settings={settings}
        />
      </>
    );
  };

  return (
    <Card className={fulfillmentCss.trackerCard} gap="xl" w="full">
      <Flex dir="column" flex={1} gap="xl">
        <DeliveryEstimate
          // @ts-expect-error: strict-null-checks
          delivery={estimatedOrActualDelivery}
          pastPreTransit={pastPreTransit}
          // @ts-expect-error: strict-null-checks
          robustTracker={robustTracker}
          settings={settings}
        />

        <StatusDisplay
          // @ts-expect-error: strict-null-checks
          delivery={estimatedOrActualDelivery}
          exceptionalStatus={mostRecentUnrecoveredExceptionalStatus}
          fulfillment={fulfillment}
          merchantName={merchantName}
          pastPreTransit={pastPreTransit}
          pickupConfig={pickupConfig}
          robustTracker={robustTracker}
          settings={settings}
          trackable={trackable}
        />

        {renderTrackingContent()}
      </Flex>
    </Card>
  );
});

// If the estimated delivery date is in the past, set it to now
// Since we don't get updates when the estimated delivery date changes
function shouldHaveBeenDeliveredButWasnt(tracker?: ExternalShipmentTracker) {
  return !!(
    tracker?.status !== "delivered" &&
    tracker?.est_delivery_date &&
    Temporal.Instant.compare(
      Temporal.Now.zonedDateTimeISO().toInstant(),
      new Date(tracker.est_delivery_date).toTemporalInstant(),
    ) > 0
  );
}

//The longer this lives here, the sadder I get
enum CustomCompanyPickUp {
  ANDAR = "andar-wallets.myshopify.com",
  IVY_CITY = "ivy-city-co.myshopify.com",
}

function customAddressPickUpBlock({
  storeUrl,
  fulfillment,
  isPickUpOrder,
}: {
  storeUrl: string;
  fulfillment?: TrackableFulfillment;
  isPickUpOrder: boolean;
}): JSX.Element | false {
  if (
    storeUrl === CustomCompanyPickUp.IVY_CITY ||
    storeUrl === CustomCompanyPickUp.ANDAR
  ) {
    if (Object.values(CustomCompanyPickUp).includes(storeUrl)) {
      const pickUpInfo = {
        [CustomCompanyPickUp.ANDAR]: {
          hours: "Monday - Friday 9am to 5pm",
          address: "4320 E Warner Rd. Suite 101 Gilbert, AZ 85296",
          condition: isAndarFulfillmentInStorePickup(fulfillment),
        },
        [CustomCompanyPickUp.IVY_CITY]: {
          hours: "Monday - Saturday 10am to 7pm",
          address: "12711 Old Liberty Way Suite 700 Riverton, Utah 84096",
          condition: isPickUpOrder,
        },
      }[storeUrl];

      return (
        pickUpInfo.condition !== false && (
          <Flex dir="column" gap="md">
            <Text fontSize="md" fontWeight="semibold">
              In store pick up hours:
            </Text>
            <Text fontSize="md">{pickUpInfo.hours}</Text>
            <Text fontSize="md" fontWeight="semibold">
              Store Address:
            </Text>
            <Text fontSize="md">{pickUpInfo.address}</Text>
          </Flex>
        )
      );
    }
  }
  return false;
}
